import {
    type App,
    computed,
    type ComputedRef,
    inject,
    readonly,
    type Ref,
    ref,
} from 'vue'
import type { Locale } from '@/i18n'
import { type ReactiveCookiePlugin } from '@/plugins/cookie-plugin'
import { type RegionPlugin } from '@/plugins/region.ts'
import type { RegionCode } from '@/graphql/api'

const currencyPluginKey = 'app.plugin.currency'
const useCurrencyKey = 'app.plugin.use-currency'

export const currencies = [
    'GBP',
    'EUR',
    'USD',
    'NZD',
    'AUD',
    'CAD',
    'ZAR',
] as const

export type AppCurrency = (typeof currencies)[number]
export const defaultCurrency: AppCurrency = 'GBP'
export type SelectCurrencyFunction = (currency: AppCurrency) => void
export type CurrencyPlugin = {
    currency: Readonly<Ref<AppCurrency | undefined>>
    selectCurrency: SelectCurrencyFunction
}

export function isAppCurrency(
    currency: string | undefined
): currency is AppCurrency {
    return currencies.findIndex((curr) => curr === currency) !== -1
}

const currencyDefaults: Record<RegionCode, AppCurrency> = {
    INT: 'USD',
    UK: 'GBP',
    EU: 'EUR',
    US: 'USD',
    IE: 'EUR',
    NZ: 'NZD',
    AU: 'AUD',
    CA: 'CAD',
    ZA: 'ZAR',
} as const

export function installCurrencyPlugin(
    app: App,
    options: {
        locale: Readonly<Ref<Locale>>
        cookies: ReactiveCookiePlugin
        regionPlugin: RegionPlugin
    }
): CurrencyPlugin {
    const { cookies } = options
    const { createCurrencyCookie } = cookies

    const currencyCookie = createCurrencyCookie()

    const currency: Ref<AppCurrency | undefined> = ref(currencyCookie.value)

    const selectCurrency: SelectCurrencyFunction = (c: AppCurrency): void => {
        // !!! Before you potentially make a mess of this:
        // We aren't exposing the currency cookie straight out of the cookie plugin on purpose
        // If cookies are disabled, then the select currency function might stop working altogether
        // So we're preserving a copy of the selected currency here and updating both the cookie and the local copy
        // The local copy is then exposed and used throughout the app
        currency.value = c
        currencyCookie.value = c
    }

    const plugin: CurrencyPlugin = Object.freeze({
        currency: readonly(currency),
        selectCurrency,
    })

    app.provide(currencyPluginKey, plugin)

    const { regionPlugin } = options
    const { region, regionCode } = regionPlugin
    const useCurrencyComputed = computed((): AppCurrency => {
        if (currency.value) {
            return currency.value
        }

        if (region.value) {
            return region.value.currency
        }

        // DO NOT FUCKING TOUCH THIS BECAUSE IT CREATES HYDRATION MISMATCH BUGS
        if (currencyDefaults[regionCode.value]) {
            return currencyDefaults[regionCode.value]
        }

        return 'GBP'
    })
    app.provide(useCurrencyKey, useCurrencyComputed)

    return plugin
}

export function useCurrencyPlugin(): CurrencyPlugin {
    return inject(currencyPluginKey) as CurrencyPlugin
}

export function useCurrency(): ComputedRef<AppCurrency> {
    return inject(useCurrencyKey) as ComputedRef<AppCurrency>
}
